import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Alert, Fade, TextField } from '@mui/material'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { INewModule } from '../../../../@types'
import { CardModule, Modal } from '../../../../components'
import { useInputText } from '../../../../hooks'
import services from '../../../../services'
import { ModalRegisterProps } from '../@types'

export default function ModalRegister({
  open,
  onClose,
  onDone,
}: ModalRegisterProps) {
  const initialValues = { level: 1, name: '', description: '' }
  const input = useInputText(initialValues)

  const [activeIcon, setActiveIcon] = useState<File | null>(null)
  const [disabledIcon, setDisabledIcon] = useState<File | null>(null)

  useEffect(() => {
    setActiveIcon(null)
    setDisabledIcon(null)
  }, [])

  const onActiveIconChange = (file: typeof activeIcon) => setActiveIcon(file)

  const onDisabledIconChange = (file: typeof disabledIcon) =>
    setDisabledIcon(file)

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const closeModal = () => {
    input.clear()
    setError(null)
    onDone()
    onClose()
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault()
      if (error) setError(null)
      setLoading(true)

      const { name, level, description } = input
      const data: INewModule = {
        name,
        description,
        level: parseInt(level as unknown as string),
        activeIcon: !activeIcon
          ? undefined
          : {
              filename: activeIcon?.name,
              contentType: activeIcon?.type,
            },
        disableIcon: !disabledIcon
          ? undefined
          : {
              filename: disabledIcon?.name,
              contentType: disabledIcon?.type,
            },
      }

      const createdModule = await services.modules.create(data)
      if (createdModule?.uploadUrls && createdModule.uploadUrls.length === 2) {
        // Dario: adicionei ! no uploadUrls[] para nao ser undefined
        const uploadActiveIcon = async () =>
          await axios.put(createdModule!.uploadUrls![0], activeIcon!, {
            headers: { 'Content-Type': activeIcon!.type },
          })
        const uploadDisableIcon = async () =>
          await axios.put(createdModule!.uploadUrls![1], disabledIcon!, {
            headers: { 'Content-Type': disabledIcon!.type },
          })

        await Promise.all([uploadActiveIcon(), uploadDisableIcon()]).catch(
          () => {
            throw new Error('failed to upload images. You can edit it later')
          },
        )

        closeModal()
      }
    } catch (error) {
      setError((error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={closeModal}
      head={<h2 className="text-xl font-bold">Registar novo Módulo</h2>}
    >
      <Fade in={open}>
        <form onSubmit={handleSubmit} className="grid gap-4">
          <ScrollArea className="h-[500px] w-full">
            <div className="grid gap-4">
              <div className="grid gap-3 w-full md:grid-cols-2">
                <CardModule
                  variant="edit"
                  editProps={{
                    label: 'Ícone desativado',
                    onIconFileChange: onDisabledIconChange,
                  }}
                />

                <CardModule
                  variant="edit"
                  editProps={{
                    label: 'Ícone ativo',
                    onIconFileChange: onActiveIconChange,
                  }}
                />
              </div>

              <TextField
                required
                label="Nome"
                name="name"
                value={input.name}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.name}
                helperText={input.errors.name}
                disabled={loading}
              />

              <TextField
                required
                type="number"
                inputProps={{ min: 1, max: 5 }}
                label="Nível"
                name="level"
                value={input.level}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.level}
                helperText={input.errors.level}
                disabled={loading}
              />

              <TextField
                required
                minRows={4}
                maxRows={8}
                multiline
                label="Descrição"
                name="description"
                value={input.description}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.description}
                helperText={input.errors.description}
                disabled={loading}
              />

              {loading && <Alert severity="warning">A enviar dados...</Alert>}
              {error && <Alert severity="error">{error}</Alert>}
            </div>
          </ScrollArea>

          <div className="w-max max-w-full ml-auto mt-4 gap-4 flex">
            <Button
              disabled={loading}
              variant="outline"
              type="reset"
              onClick={closeModal}
            >
              Cancelar
            </Button>
            <Button disabled={loading} type="submit">
              Criar
            </Button>
          </div>
        </form>
      </Fade>
    </Modal>
  )
}
